import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { SortColumn } from "react-data-grid";
import { useNavigate } from "react-router-dom";

import { CompleteBlueIcon } from "../../../../../assets";
import { ProjectPhase } from "../../../../../constants/project-phase";
import { TaskConstants } from "../../../../../constants/workflow/TaskConstants";
import { CursorChangeProps, Origin, ResultType, TaskPanelData } from "../../../../../models";
import {
  GetProjectDetailsResponse,
  GetTaskBoardResponse,
  searchWorkflowTasks,
  SearchWorkflowTasksResponse,
} from "../../../../../service/query";
import { getTaskBoard } from "../../../../../service/query/QueryService.full";
import { ResultData, Status } from "../../../../../service/Shared";
import { useAuth } from "../../../../../useAuth";
import { flattenObject } from "../../../../../utils";
import { getWorkflowTasksBoardRoute } from "../../../../../utils/routes";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  dataGridMapFilterCriteria,
} from "../../../../../widget";
import { AvatarCellFormatter } from "../../../../../widget/data/DataGrid/components/CellFormatters";
import { IconCellFormatterData } from "../../../../../widget/data/DataGrid/models";
import { ProjectContext } from "../ProjectContext";

interface UseTasksReturnData {
  columns: DataGridColumnDefinition[];
  defaultSortingCriteria: SortColumn[];
  dataIsLoading: boolean;
  projectDetails: GetProjectDetailsResponse | undefined;

  showSelectWorkflowTemplateModal: boolean;
  setShowSelectWorkflowTemplateModal: Dispatch<SetStateAction<boolean>>;

  showCompleteTaskModal: boolean;
  handleCloseCompleteTaskModal: () => void;

  showWorkflowTaskPanel: boolean;
  setShowWorkflowTaskPanel: Dispatch<SetStateAction<boolean>>;
  handleCloseWorkflowTaskPanel: () => void;

  selectedPanelTask: SearchWorkflowTasksResponse["results"][0] | undefined;
  selectedCompleteTask: TaskPanelData | undefined;

  workflow: GetTaskBoardResponse["results"][number] | undefined;

  refreshTasks: boolean;
  handleRefresh: () => void;

  navigateToTasksBoard: () => void;
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;
}

export const useDocuments = (): UseTasksReturnData => {
  const navigate = useNavigate();
  const { currentUserType } = useAuth();
  const { projectDetails } = useContext(ProjectContext);

  const [dataIsLoading, setDataIsLoading] = useState(true);
  const [refreshTasks, setRefreshTasks] = useState(false);

  const [workflow, setWorkflow] = useState<GetTaskBoardResponse["results"][number] | undefined>();

  const [selectedCompleteTask, setSelectedCompleteTask] = useState<TaskPanelData>();
  const [selectedPanelTask, setSelectedPanelTask] = useState<SearchWorkflowTasksResponse["results"][0]>();

  const [showSelectWorkflowTemplateModal, setShowSelectWorkflowTemplateModal] = useState(false);
  const [showCompleteTaskModal, setShowCompleteTaskModal] = useState(false);
  const [showWorkflowTaskPanel, setShowWorkflowTaskPanel] = useState(false);

  const handleCloseCompleteTaskModal = (): void => {
    setSelectedCompleteTask(undefined);
    setShowCompleteTaskModal(false);
  };

  const handleCloseWorkflowTaskPanel = (): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  };

  const handleRefresh = (): void => {
    setRefreshTasks((prev) => !prev);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
    {
      name: "Task",
      key: "nameLink",
      dataType: "string",
      formatter: "buttonLink",
      minWidth: 300,
      filterable: true,
      sortable: true,
    },
    {
      name: "Assigned to",
      key: "avatar",
      dataType: "string",
      formatter: "custom",
      alignment: "center",
      customCellFormatter: AvatarCellFormatter,
      filterable: true,
      minWidth: 70,
    },
    {
      key: "status",
      name: "Status",
      dataType: "string",
      formatter: "stringWithEllipsis",
      filterable: true,
      sortable: true,
      minWidth: 140,
    },
    {
      key: "deadlineDate",
      name: "Deadline",
      dataType: "Date",
      formatter: "dateOnlyWithIndicator",
      minWidth: 140,
      filterable: false,
      sortable: true,
    },
    {
      key: "updatedAt",
      name: "Date updated",
      dataType: "Date",
      formatter: "dateOnly",
      filterable: false,
      minWidth: 140,
    },
    {
      name: "Complete",
      key: "completeIcon",
      dataType: "boolean",
      formatter: "icon",
      alignment: "center",
      filterable: false,
      sortable: false,
      minWidth: 70,
    },
  ];

  const defaultSortingCriteria: SortColumn[] = [{ columnKey: "deadlineDate", direction: "ASC" }];
  const navigateToTasksBoard = (): void => {
    if (projectDetails?.workflowUuid) {
      navigate(
        getWorkflowTasksBoardRoute(projectDetails.workflowUuid, currentUserType, { origin: Origin.ProjectTasksPage })
      );
    }
  };

  const formatData = useCallback((responseData: SearchWorkflowTasksResponse | undefined): ResultData[] => {
    return (
      responseData?.results?.map((d) => {
        const result = flattenObject(d) as ResultData & { avatar: { fullName?: string } };

        result.avatar = { fullName: d.assignee?.name };
        result.nameLink = {
          text: d.name,
          action: () => {
            setSelectedPanelTask(d);
            setShowWorkflowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;

        if (d.status !== TaskConstants.TASK_STATUS_UNASSIGNED)
          result.completeIcon = {
            action: () => {
              setSelectedCompleteTask({
                deadlineDate: d.deadlineDate,
                isMandatory: d.isMandatory,
                isMilestone: d.isMilestone,
                keyDate: d.keyDate,
                status: d.status,
                repeat: d.repeat,
                description: d.description,
                name: d.name,
                visibility: [],
                assignedToUserUuid: d.assignee?.uuid ?? null,
                assignedToUserName: d.assignee?.name ?? null,
                ownerOrganisationUuid: d.ownerOrganisation.uuid,
                relateToActivity: d.objectType === "Activity",
                activityUuid: d.objectType === "Activity" ? d.objectUuid : null,
                stepUuid: d.step.uuid,
                phase: d.step.phase as ProjectPhase,
                uuid: d.uuid,
                rowVersion: d.rowVersion,
                resolution: d.resolution,
              });
              setShowCompleteTaskModal(true);
            },
            icon: <CompleteBlueIcon width={20} height={20} strokeWidth={1.5} />,
          } as IconCellFormatterData;

        return result;
      }) || []
    );
  }, []);

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 0,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    if (projectDetails === undefined) {
      return data;
    }

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    if (filterCriteria.nameLink) {
      filterCriteria.name = filterCriteria.nameLink;
      filterCriteria.nameLink = undefined;
      delete filterCriteria.nameLink;
    }

    filterCriteria.workflowObjectUuid = {
      operator: "eq",
      value: projectDetails.uuid,
    };

    if (filterCriteria.avatar) {
      filterCriteria.assignee = {
        name: filterCriteria.avatar,
      };
      delete filterCriteria.avatar;
    }

    const sortKeyMap: Record<string, string> = {
      nameLink: "name",
      avatar: "assignee.name",
    };

    await searchWorkflowTasks(
      {
        paging: {
          limit: paging.pageSize,
          beforeCursor: paging.beforeCursor || null,
          afterCursor: paging.afterCursor || null,
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        sort: sorting.map((sort: { key: any; direction: any }) => ({
          key: sortKeyMap[sort.key] || sort.key,
          direction: sort.direction,
        })),
        filter: { results: filterCriteria },
      },
      {
        operator: "nin",
        value: [TaskConstants.TASK_STATUS_COMPLETE],
      }
    )
      .then((response) => {
        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setDataIsLoading(false);
      });

    return data;
  };

  const getAndSetWorkflow = async (): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filterResults: any = {
      projectUuid: {
        operator: "eq",
        value: projectDetails?.uuid,
      },
    };

    const res = await getTaskBoard({
      showComplete: true,
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 1,
      },
      filter: {
        results: filterResults,
      },
    });

    if (res.status === Status.Success && res.data) {
      setWorkflow(res.data.results[0]);
    }
  };

  useEffect(() => {
    if (projectDetails?.uuid) {
      getAndSetWorkflow();
    }
  }, [projectDetails?.uuid]);

  return {
    columns,
    defaultSortingCriteria,
    dataIsLoading,
    projectDetails,

    showCompleteTaskModal,
    handleCloseCompleteTaskModal,

    showWorkflowTaskPanel,
    setShowWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,

    showSelectWorkflowTemplateModal,
    setShowSelectWorkflowTemplateModal,

    selectedPanelTask,
    selectedCompleteTask,
    workflow,

    refreshTasks,
    handleRefresh,

    navigateToTasksBoard,
    onChange,
  };
};
